import { Link, useLocation, useNavigate } from "react-router-dom";

import { ReactComponent as Logo } from "../../assets/icons/Logo.svg";
import { ReactComponent as Logout } from "../../assets/icons/logout.svg";
import { ReactComponent as SmallLogo } from "../../assets/icons/small-logo.svg";
import { ReactComponent as DesktopIcon } from "../../assets/icons/desktop.svg";
import { ReactComponent as MetricsIcon } from "../../assets/icons/metrics.svg";
import { ReactComponent as CertificationIcon } from "../../assets/icons/certification.svg";
import { ReactComponent as DashboardIcon } from "../../assets/icons/dashboard.svg";
import { ReactComponent as TeamIcon } from "../../assets/icons/team.svg";
import { ReactComponent as UserIcon } from "../../assets/icons/user.svg";
import { ReactComponent as BadgeIcon } from "../../assets/icons/award-outline.svg";
import { ReactComponent as MicrophoneIcon } from "../../assets/icons/micro-phone.svg";
import { ReactComponent as SupportIcon } from "../../assets/icons/support.svg";
import { ReactComponent as EngagementReportIcon } from "../../assets/icons/chart.svg";
import { ReactComponent as SettingsIcon } from "../../assets/icons/setting-outline.svg";
import { ReactComponent as HelpCircleIcon } from "../../assets/icons/help-circle.svg";
import { useApp } from "../../services/app-context";
import { MENU_NAMES, userRoles } from "../../constant";
import { useContext, useEffect, useMemo, useState } from "react";
import useResponsive from "../../services/useResponsive";
import { TutorialContext } from "../../contexts/TutorialContext";
import Modal from "../Modal";
import { Button } from "../Button";
import { ReactComponent as ChevronDownIcon } from "../../assets/icons/chevron-down.svg";
import { isAdminSettingsEnabled, isBadgeCenterEnabled, isCertificationModeEnabled, isEngagementReportDisabled, isMyProfileEnabled } from "../../integrations/posthog/featureFlags";
import { getMenuLabel, getUrl } from "../../pages/certifications/utils/labels";

export const EMPTY_MENU_LABELS: Record<string, string> = {};
export const EMPTY_ROUTE_NAMES: Record<string, string> = {};


export function MenuItem({ item, collapsed, isSelected, index, level = 0, tenantProfile, user, onClick, menuLabels }: { item: any, collapsed: boolean, isSelected: (path: string) => boolean, index: number, level?: number, tenantProfile: any, user: any, onClick: (name: string) => void, menuLabels: any }) {
  const [isOpen, setIsOpen] = useState(false);

  const checkMenuShouldOpen = (nestedItems: any[]) => {
    return nestedItems.some((nestedItem) => nestedItem.path && isSelected(nestedItem.pathName));
  }

  // Auto-open when navigating to a nested route; never auto-close so user/tutorial-opened state persists
  useEffect(() => {
    if (item.nestedItems && checkMenuShouldOpen(item.nestedItems)) {
      setIsOpen(true);
    }
  }, [item.nestedItems]);

  if (item.nestedItems) {
    const filteredNestedItems = item.nestedItems?.filter((nestedItem) => checkPermission(nestedItem, tenantProfile, user));
    return (
      <>
        <button
          {...(item.title === getMenuLabel("tools", menuLabels) ? { "data-tutorial-tools-toggle": true } : {})}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full py-2.5 ${item.colorType === "fill" ? "[&_path]:!fill-text-link" : "[&_path]:!stroke-text-link"} rounded-[12px] justify-start items-center gap-3 flex group hover:bg-white/[0.08] transition duration-75 hover:scale-[1.02] ${collapsed ? " justify-center px-0" : "pl-4 pr-3"}`}
        >
          {!collapsed && (
            <div className="grow shrink basis-0 text-text-link text-[17px] font-[590] flex items-center justify-between gap-4">
              {item.title}
              <div className="flex items-center gap-2">
                <span className="text-text-link text-[17px] font-normal">{filteredNestedItems.length}</span>
                {isOpen ? <ChevronDownIcon className="w-4 h-4 mt-0.5" /> : <ChevronDownIcon className="w-4 h-4 rotate-[270deg] mt-0.5" />}
              </div>
            </div>
          )}
        </button>
        {isOpen && (
          <div className="flex flex-col gap-2 w-full">
            {filteredNestedItems.map((nestedItem, nestedIndex) => (
              <MenuItem level={level + 1} item={nestedItem} collapsed={collapsed} isSelected={isSelected} index={nestedIndex} key={nestedIndex} tenantProfile={tenantProfile} user={user} onClick={onClick} menuLabels={menuLabels} />
            ))}
          </div>
        )}
      </>
    );
  }
  if (!item.path) {
    return (
      <div
        role="button"
        onClick={() => onClick(item.name)}
        key={index}
        className={`${item.colorType === "fill" ? "[&_path]:!fill-text-link" : "[&_path]:!stroke-text-link"} w-full py-2.5 rounded-[12px] justify-start items-center gap-3 flex group hover:bg-white/[0.08] transition duration-75 hover:scale-[1.02] ${collapsed ? " justify-center px-0" : "px-6"} ${level > 0 && !collapsed ? "!pl-[30px]" : ""}`}
      >
        <item.Icon />

        {!collapsed && (
          <div className="grow shrink basis-0 text-text-link text-sm">
            {item.title}
          </div>
        )}
      </div>
    );
  }
  return (
    <Link
      to={item.path}
      key={index}
      data-tutorial-dashboard-nav={item.title === getMenuLabel("dashboard", menuLabels) ? true : undefined}
      data-tutorial-praxisplay-nav={item.title === getMenuLabel("praxisPlay", menuLabels) ? true : undefined}
      className={`w-full py-2.5 ${item.colorType === "fill" ? "[&_path]:!fill-text-link" : "[&_path]:!stroke-text-link"} rounded-[12px] justify-start items-center gap-3 flex group hover:bg-white/[0.08] transition duration-75 hover:scale-[1.02] ${isSelected(item.pathName) ? "!bg-menuHighlight" : ""
        } ${collapsed ? " justify-center px-0" : "px-6"} ${level > 0 && !collapsed ? "!pl-[30px]" : ""}`}
    >
      <item.Icon />

      {!collapsed && (
        <div className="grow shrink basis-0 text-text-link text-sm">
          {item.title}
        </div>
      )}
    </Link>
  );
}

export const menuItems = (menuLabels: any, routeNames: any) => {
  return [
    {
      title: getMenuLabel("dashboard", menuLabels),
      Icon: DashboardIcon,
      path: "/dashboard",
      pathName: "/dashboard",
    },
    {
      title: getMenuLabel("badgeCenter", menuLabels),
      Icon: BadgeIcon,
      path: "/badge-center",
      pathName: "/badge-center",
      needBadgeCenterEnabled: true,
    },
    {
      title: getMenuLabel("myProfile", menuLabels),
      Icon: UserIcon,
      name: MENU_NAMES.myProfile,
      needMyProfileEnabled: true,
    },

    {
      title: getMenuLabel("team", menuLabels),
      Icon: TeamIcon,
      path: "/team-dashboard",
      pathName: "/team-dashboard",
      roles: [userRoles.Manager, userRoles.Trainer, userRoles.Admin],
    },
    {
      title: getMenuLabel("engagementReport", menuLabels),
      Icon: EngagementReportIcon,
      path: "/engagement-report",
      pathName: "/engagement-report",
      roles: [userRoles.Admin],
      needEngagementReportEnabled: true,
    },
    {
      title: getMenuLabel("tools", menuLabels),
      Icon: MetricsIcon,
      nestedItems: [
        {
          title: getMenuLabel("praxisPlay", menuLabels),
          Icon: DesktopIcon,
          path: "/praxisplay/role-play",
          pathName: "/praxisplay",
        },
        {
          title: getMenuLabel("certifications", menuLabels),
          Icon: CertificationIcon,
          path: `/${getUrl("certificationRoute", routeNames)}`,
          pathName: `/${getUrl("certificationRoute", routeNames)}`,
          needCertificationMode: true,
        },
      ]
    },
    {
      title: getMenuLabel("support", menuLabels),
      Icon: SupportIcon,
      nestedItems: [
        {
          title: getMenuLabel("admin", menuLabels),
          Icon: SettingsIcon,
          path: "/admin-settings",
          pathName: "/admin-settings",
          roles: [userRoles.Admin],
          needAdminSettingsEnabled: true,
        },
        {
          title: getMenuLabel("getInTouch", menuLabels),
          Icon: MicrophoneIcon,
          path: "/support",
          pathName: "/support",
        },
        {
          title: getMenuLabel("faqs", menuLabels),
          Icon: HelpCircleIcon,
          path: "/faqs",
          pathName: "/faqs",
        },
        {
          title: getMenuLabel("tutorials", menuLabels),
          Icon: HelpCircleIcon,
          name: MENU_NAMES.tutorials,
        },
      ]
    },
  ];
};

export const checkPermission = (menu, tenantProfile, user) => {
  if (menu.nestedItems) {
    return menu.nestedItems.some((nestedItem) => checkPermission(nestedItem, tenantProfile, user));
  }
  // if the menu has roles and the user is not in the roles, return false
  if (menu.roles && !menu.roles?.includes(user?.role)) {
    return false;
  }
  if (menu.needCertificationMode) {
    const isCertificationMode = isCertificationModeEnabled();
    if (isCertificationMode) {
      return true;
    }
    return false;
  }
  if (menu.needMyProfileEnabled) {
    const isMyProfile = isMyProfileEnabled();
    if (isMyProfile) {
      return true;
    }
    return false;
  }
  if (menu.needAdminSettingsEnabled) {
    const isAdminSettings = isAdminSettingsEnabled();
    if (isAdminSettings) {
      return true;
    }
    return false;
  }
  if (menu.needBadgeCenterEnabled) {
    const isBadgeCenter = isBadgeCenterEnabled();
    if (isBadgeCenter) {
      return true;
    }
    return false;
  }
  if (menu.needEngagementReportEnabled) {
    return !isEngagementReportDisabled();
  }
  return true;
};

export function Sidebar({
  user,
  onEdit,
  userInfoLoading,
}: {
  user: any;
  onEdit: () => void;
  userInfoLoading: boolean;
}) {
  const { isTablet } = useResponsive();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { tenantProfile, styles, preLoginLogos, fetchPreLoginLogos } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const menuLabels = tenantProfile?.menuLabels ?? EMPTY_MENU_LABELS;
  const routeNames = tenantProfile?.routeNames ?? EMPTY_ROUTE_NAMES;

  const isSelected = (path) => {
    return location.pathname.includes(path);
  };

  const handleLogout = () => {
    setConfirmLogout(true);
  };

  const onLogout = () => {
    setConfirmLogout(false);
    navigate('/signout');
  };

  const menus = useMemo(() => {
    if (userInfoLoading) {
      return menuItems(menuLabels, routeNames);
    }
    if (user?.role === userRoles.Manager) {
      return [
        ...menuItems(menuLabels, routeNames),
        // {
        //   title: "Users",
        //   Icon: UsersIcon,
        //   path: "/users",
        // },
      ];
    }
    return menuItems(menuLabels, routeNames);
  }, [user, userInfoLoading, menuLabels, routeNames]);

  const tutorialContext = useContext(TutorialContext);

  const handleClick = (name: string) => {
    if (name === MENU_NAMES.myProfile) {
      onEdit();
    }
    if (name === MENU_NAMES.tutorials) {
      tutorialContext?.resetTutorial();
      navigate("/dashboard");
    }
  }

  useEffect(() => {
    if (isTablet) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [isTablet]);

  return (
    <>
      <div data-testid="sidebar" className="hidden lg:flex max-h-[100vh] min-w-[272px] py-3.5 pl-3.5 bg-background">
        <div className="hidden w-full lg:flex max-h-[calc(100vh-28px)] overflow-auto flex-col border-r border-gray-200 rounded-[20px]"
          style={{ background: "linear-gradient(180deg, var(--menuPrimaryBg) 0%, var(--menuSecondaryBg) 100%)" }}>
          <div
            className={`self-stretch py-8 border-b border-white/20 justify-center items-center flex ${collapsed ? "px-7" : "px-12"
              }`}
          >
            <div className="justify-center items-center flex relative">
              {!collapsed ? preLoginLogos?.["logo"] ? <img
                onError={() => {
                  fetchPreLoginLogos();
                }}
                src={preLoginLogos?.["logo"]} alt="logo" className="max-w-[176px] max-h-[40px]" /> : <Logo /> : <SmallLogo />}
            </div>
          </div>
          <div className="flex h-[calc(100vh-80px)] justify-between flex-col">
            <div className="flex-col justify-start items-start gap-5 flex relative">
              <div className="flex-col justify-start items-start flex w-full p-4 gap-2">
                {menus
                  .filter((item) => checkPermission(item, tenantProfile, user))
                  .map((item, index) => (
                    <MenuItem item={item} collapsed={collapsed} isSelected={isSelected} index={index} key={index} tenantProfile={tenantProfile} user={user} onClick={handleClick} menuLabels={menuLabels} />
                  ))}
              </div>
            </div>
            <div className="flex flex-col gap-3 relative">
              <div className="mb-4 flex justify-center items-center">
                <button
                  onClick={handleLogout}
                  className={`text-center items-center gap-1 flex group-hover:opacity-80 text-text-link text-base font-normal ${collapsed ? "py-2" : ""
                    }`}
                >
                  <Logout
                    className={`w-5 h-5 relative group-hover:opacity-80 [&_path]:stroke-text-link`}
                  />
                  {!collapsed && "Logout"}
                </button>
              </div>
              {/* <div
              className={`mb-4 p-5 bg-menuProfileBg rounded-2xl border border-white border-opacity-20 flex-col justify-start items-center gap-3 inline-flex ${collapsed ? "w-[72px] mx-2.5 !py-3" : "mx-4"
                }`}
            >
              <div className={`${collapsed ? "w-12 h-12" : "w-16 h-16"}`}>
                {user?.profileImg ? (
                  <ImageView
                    className="w-full h-full rounded-full object-cover"
                    src={user?.profileImg}
                    alt="user"
                  />
                ) : (
                  <div
                    className={`bg-primary w-full h-full rounded-full text-text-link flex justify-center items-center font-semibold ${collapsed ? "text-xl" : "text-3xl"
                      }`}
                  >
                    {getInitial(user?.given_name, user?.family_name)}
                  </div>
                )}
              </div>
              {!collapsed && (
                <div
                  className="text-center text-text-link text-base font-bold max-w-[225px] overflow-hidden text-ellipsis whitespace-nowrap"
                  title={`${user.given_name} ${user.family_name}`}
                >
                  {user.given_name} {user.family_name}
                </div>
              )}
              <div
                className={`justify-start items-center gap-3.5 flex ${collapsed ? "flex-col" : "flex-row"
                  }`}
              >
                <div className="justify-start items-center gap-1 group flex">
                  <button
                    onClick={onEdit}
                    className={`flex items-center gap-1 text-center group-hover:opacity-80 text-text-link text-base font-normal ${collapsed ? "py-2" : ""
                      }`}
                  >
                    <Pencil
                      className={`w-5 h-5 relative group-hover:opacity-80 [&_path]:stroke-text-link`}
                    />
                    {!collapsed && "Edit"}
                  </button>
                </div>
                {!collapsed && (
                  <div className="border-l border-[#C4BEDC] h-[80%]"></div>
                )}
                <div className="justify-start group">
                  <button
                    onClick={handleLogout}
                    className={`text-center items-center gap-1 flex group-hover:opacity-80 text-text-link text-base font-normal ${collapsed ? "py-2" : ""
                      }`}
                  >
                    <Logout
                      className={`w-5 h-5 relative group-hover:opacity-80 [&_path]:stroke-text-link`}
                    />
                    {!collapsed && "Logout"}
                  </button>
                </div>
              </div>
            </div> */}
            </div>
          </div>
        </div>
      </div>
      <Modal contentClassName="w-[325px]" isOpen={confirmLogout} handleModal={() => setConfirmLogout(false)}>
        <div className="flex flex-col items-center gap-4">
          <p className="text-[15px] text-text2">Are you sure you want to log out?</p>
          <div className="flex justify-end gap-2">
            <Button variant="primary" onClick={() => setConfirmLogout(false)} className="!bg-background3 !text-black px-5 h-[35px] !rounded-full !border-0">Cancel</Button>
            <Button variant="primary" onClick={onLogout} className="px-5 py-0 !h-[35px] !rounded-full">Logout</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
